import { ArrowRightIcon } from '@heroicons/react/20/solid';
import { animate, motion, useMotionValue, useTransform } from 'framer-motion';
import React from 'react';

import { FadeSlideIn } from './Animations';
import { cn } from './cn';
import { Loader } from './Loader';

export interface StatTileProps {
  label: string;
  /** Numeric values get a count-up animation; strings render as-is. */
  value?: number | string;
  /** Rendered directly after the value (e.g. "+" for truncated counts or a unit). */
  suffix?: string;
  icon?: React.ReactNode;
  /** Short caption under the value. */
  hint?: string;
  onClick?: () => void;
  loading?: boolean;
  /** Entrance animation delay in seconds (stagger tiles in a row). */
  delay?: number;
  className?: string;
  dataTest?: string;
}

const CountUpValue = ({ value }: { value: number }) => {
  const motionValue = useMotionValue(0);
  const rounded = useTransform(motionValue, (latest) => Math.round(latest).toLocaleString());
  React.useEffect(() => {
    const controls = animate(motionValue, value, { duration: 0.8, ease: 'easeOut' });
    return () => controls.stop();
  }, [motionValue, value]);
  return <motion.span>{rounded}</motion.span>;
};

/**
 * Hero-number tile for dashboards: one dominant value with a muted label.
 * Clickable tiles render as a button and navigate on click.
 */
export const StatTile = ({
  label,
  value,
  suffix,
  icon,
  hint,
  onClick,
  loading,
  delay,
  className,
  dataTest,
}: StatTileProps) => {
  const body = (
    <>
      <div className="flex items-center gap-2">
        {icon && (
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
            {icon}
          </span>
        )}
        <span className="type-label text-text-muted">{label}</span>
        {onClick && (
          <ArrowRightIcon className="ml-auto h-4 w-4 text-text-muted transition-transform duration-200 group-hover:translate-x-0.5" />
        )}
      </div>
      {loading ? (
        <div className="flex grow items-center">
          <Loader size="sm" />
        </div>
      ) : (
        <div className="flex items-baseline gap-1">
          <span className="text-3xl font-bold tabular-nums text-text-primary">
            {typeof value === 'number' ? <CountUpValue value={value} /> : value ?? '—'}
          </span>
          {suffix && <span className="type-body-sm text-text-secondary">{suffix}</span>}
        </div>
      )}
      {hint && !loading && <span className="type-caption text-text-muted">{hint}</span>}
    </>
  );

  const surfaceClassName = cn(
    'flex h-full flex-col gap-2 rounded-xl border border-border bg-surface p-5 text-left shadow-sm',
    onClick &&
      'group cursor-pointer transition-shadow duration-200 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2 focus-visible:ring-offset-focus-ring-offset',
    className
  );

  return (
    <FadeSlideIn className="h-full" delay={delay} offset={12}>
      {onClick ? (
        <button type="button" onClick={onClick} data-test={dataTest} className={cn(surfaceClassName, 'w-full')}>
          {body}
        </button>
      ) : (
        <div data-test={dataTest} className={surfaceClassName}>
          {body}
        </div>
      )}
    </FadeSlideIn>
  );
};
